/**
 * Common 模块入口和初始化
 * 整合所有通用工具方法和 UI 组件，提供统一的初始化入口
 * 
 * @module common/index
 * @author wei<innereyecw@gmail.com>
 * @version 5.0.0
 */
(function (global) {
  "use strict";

  // 确保命名空间存在
  if (!global.watashi) {
    global.watashi = {};
  }
  if (!global.watashi.common) {
    global.watashi.common = {};
  }

  const watashi = global.watashi;
  const common = watashi.common;
  const logger = (watashi.core && watashi.core.logger) || console;
  const version = "5.0.0";

  // ==================== 模块初始化 ====================
  /**
   * 初始化 common 模块
   */
  common.init = function () {
    common.name = 'watashi.common';
    common.version = version;
    logger.log(`✓ Common v${version}`);
  };

  // ==================== 统一初始化入口 ====================
  /**
   * 通用初始化函数（由调用方决定何时执行）
   * 包含所有页面通用的初始化逻辑
   */
  common.initialize = function () {
    const client = common.getClientEnvironment();

    // 1. 非移动端包装器
    if (!client.isMobile) {
      common.wrapChildElement({
        parentEl: document.body,
        parentStyle: 'background-color: #f4f4f5;',
        wrapperTag: 'div',
        wrapperId: 'ismob',
        wrapperClass: '',
        wrapperStyle: 'width: 360px; margin: auto; height: auto; border: 1px solid #ccc;background-color:#ffffff;',
      });
    }

    // 2. 初始化 REM 单位
    common.initRem();
    common.registerEvent(window, 'orientationchange', common.initRem);
    common.registerEvent(window, 'resize', common.initRem);

    // 3. 首页 Banner Swiper
    if (common.exists('#home_banner')) {
      const swiperSelector = '#site_home__brand_banner';
      new common.CreateSwiper({
        container: swiperSelector,
        swiperParams: {
          autoplay: {delay: 5000},
          pagination: {
            el: `${swiperSelector} .swiper-pagination`,
            clickable: true
          },
          autoHeight: true,
        }
      });
    }

    // 4. 粘性效果初始化
    if (common.exists('.sticky')) {
      const stickyElements = document.querySelectorAll('.sticky');
      stickyElements.forEach(function (element) {
        const stickyEffectUniqueId = Date.now().toString(36) + Math.random().toString(36).substring(2, 7);
        element.id = stickyEffectUniqueId;
        new common.StickyEffect({
          container: `#${stickyEffectUniqueId}.sticky`,
          offset: 0,
          type: element.dataset.type || 'default',
        });
      });
    }

    // 5. 回到顶部按钮
    if (common.exists('#backTop')) {
      common.setupBackToTopButton({
        selector: '#backTop',
      });
    }

    // 6. 价格格式化
    if (common.exists('.price')) {
      common.initializePriceFormatting({
        selector: '.price',
      });
    }
    if (common.exists('#product_list_container')) {
      common.initializePriceFormatting({
        selector: '.product__price',
      });
    }

    // 7. 汉堡菜单：地区店铺查询
    if (common.exists('#regional_store_query')) {
      new common.HamburgerMenu({
        container: '#regional_store_query',
        hoverClass: 'hover',
        configs: [
          {
            selector: '.regional-store > .g-name',
            container: '.regional-store > .g-container',
          },
        ]
      });
    }

    // 8. 分类菜单：产品分类页
    if (common.exists('#product_category_main')) {
      const categoryPageMenuInstance = new common.CategoryMenu({
        mainContainer: '#product_category_main',
        hoverClass: 'hover',
        container: '.category-container',
        button: '.category-container > .classification',
        subContainer: '.category-container > .sub-category',
        subCategory: '.sub-name',
      });
      if (common.exists('.category-container')) {
        categoryPageMenuInstance.fixOnLoad();
      }
    }

    // 9. 分类菜单：详细分类
    if (common.exists('#detailed_classification')) {
      new common.CategoryMenu({
        mainContainer: '#detailed_classification',
        hoverClass: 'hover',
        container: '.classification',
        button: '.classification > .class-name',
        subContainer: '.classification > .class-container',
        subCategory: '.class-name',
      });
    }

    // 10. 分类菜单：左侧边栏
    if (common.exists('#site_sidebar_left')) {
      new common.CategoryMenu({
        mainContainer: '#site_sidebar_left',
        hoverClass: 'selected',
        container: '.second-menus',
        button: '.second-menus > .sec-link.more-classes',
        subContainer: '.second-menus > .submenu',
        subCategory: '.sub',
      });

      new common.CategoryMenu({
        mainContainer: '#site_sidebar_left .submenu#submenu',
        hoverClass: 'hover',
        container: '.sub',
        button: '.sub > .name',
        subContainer: '.sub > .sub-menu',
        subCategory: '',
      });
    }

    // 11. 滑动菜单：最新产品
    if (common.exists('#newest_menu .menu-container')) {
      new common.SlideMenu({
        container: '#newest_menu .menu-container',
        menuSelector: '#newest_menu .menu-container a',
        selectedSelector: '#newest_menu .menu-container a.hover',
        speed: 150,
        navigation: {
          prevEl: '#newest_menu .button.left',
          nextEl: '#newest_menu .button.right',
        },
      });
    }

    // 12. 隐藏特定品牌店铺按钮
    if (common.exists('meta[name="Product:BrandID"]') &&
      common.exists('#product_information') &&
      common.exists('.view-shop')) {
      const brandID = document.querySelector('meta[name="Product:BrandID"]').getAttribute('content');
      const brandGroup = ["5F10", "7A10", "1P10"];
      const viewShopElement = document.querySelector('.view-shop');
      if (brandGroup.includes(brandID)) {
        viewShopElement.classList.add('hidden-none');
      }
    }

    // 13. URL 链接处理映射
    const actionMap = {
      urlOpen: {
        selfOpen: createUrlHandler('url'),
      },
    };

    ['product', 'brand', 'store', 'category'].forEach((dataType) => {
      actionMap[dataType] = {
        selfOpen: createUrlHandler('url'),
      };
    });

    // 14. 绑定点击事件
    bindClickEvents(actionMap);

    logger.log('Common module initialized successfully');
  };

  // ==================== 辅助函数 ====================

  /**
   * 创建 URL 处理器
   * @param {string} urlAttribute - URL属性名
   * @returns {Function} 事件处理函数
   */
  function createUrlHandler(urlAttribute) {
    return (element) => {
      const urlElement = element.closest(`[data-${urlAttribute}]`);
      if (!urlElement) return;
      const url = urlElement.getAttribute(`data-${urlAttribute}`);
      common.openLink(url, '', '_self');
    };
  }

  /**
   * 绑定全局点击事件
   * @param {Object} actionMap - 动作映射表
   */
  function bindClickEvents(actionMap) {
    // 绑定特定选择器的点击事件
    ['#home_brand_top > div[data-url]',
      '.pro_list>ul[data-url]', '.news_list>ul[data-url]', '.brand_list>ul[data-url]'].forEach(selector => {
      if (document.querySelector(selector)) {
        document.querySelectorAll(selector).forEach(element => {
          common.registerEvent(element, 'click', handleUrlClick);
        });
      }
    });

    // 绑定全局点击事件
    common.registerEvent(document, 'click', function (event) {
      handleGlobalClick(event, actionMap);
    }, false);
  }

  /**
   * 处理 URL 点击
   * @param {Event} event - 点击事件
   */
  function handleUrlClick(event) {
    const urlElement = event.target.closest('[data-url]');
    if (!urlElement) return;
    const url = urlElement.dataset.url;
    common.openLink(url, '');
  }

  /**
   * 处理全局点击事件
   * @param {Event} event - 点击事件
   * @param {Object} actionMap - 动作映射表
   */
  function handleGlobalClick(event, actionMap) {
    // 处理全局点击容器
    if (event.target.matches('.global-click-container')) {
      handleSpecialClick(event);
      event.stopPropagation();
      return;
    }

    if (event.target.matches('.clickable-through-container')) {
      return;
    }

    const {target} = event;
    const {type: t, action: actionValue} = target.dataset;

    // 尝试从 actionMap 中获取处理器
    let handler = actionMap[t] && actionMap[t][actionValue];
    if (typeof handler === 'function') {
      handler(target);
    } else if (t) {
      // 仅在调试模式下输出
      // logger.log(`No handler: type=${t}, action=${actionValue}`, 'warn');
    }
  }

  /**
   * 处理特殊点击（返回顶部、侧边栏等）
   * @param {Event} event - 点击事件
   */
  function handleSpecialClick(event) {
    const {target} = event;
    const {type: t} = target.dataset;

    switch (t) {
      case "backTop":
        common.scrollToTop();
        break;
      case "globalSidebarOpen":
      case 'globalSidebarClose':
        toggleSidebar(t);
        break;
      default:
        logger.error(`Type of '${t}' not found.`);
        event.stopPropagation();
    }
  }

  /**
   * 切换侧边栏显示状态
   * @param {string} action - 动作：'globalSidebarOpen' | 'globalSidebarClose'
   */
  function toggleSidebar(action) {
    const sidebar = document.querySelector('#site_sidebar_left');
    const mask = document.querySelector('#site_sidebar_mask');
    const activeClass = 'this-active';

    if (!sidebar) {
      return;
    }

    if (action === 'globalSidebarClose') {
      sidebar.classList.remove(activeClass);
      mask.classList.remove(activeClass);
      return;
    }

    mask.classList.add(activeClass);
    sidebar.classList.add(activeClass);
  }

  // ==================== 暴露公共 API ====================
  common.logger = logger;  // 暴露 logger 供外部使用

  // ==================== 文档就绪后自动初始化 ====================
  if (typeof document.ready === 'function') {
    document.ready(function () {
      common.init();
      common.initialize();
    });
  }

})(typeof window !== "undefined" ? window : {});
